import { supabase } from '@/lib/supabase';
import { AnalysisMode } from '@/types/trading-analysis-types';

// Multi-threshold optimization types
export interface MultiThresholdRequest {
  fileId: string;
  thresholds: number[];
  maxSequenceLength: number;
  analysisMode: AnalysisMode;
}

export interface ThresholdResult {
  threshold: number;
  accuracy: number;
  precision: number;
  recall: number;
  totalPredictions: number;
  skipRate: number;
  recommendationScore: number;
}

export interface MultiThresholdResult {
  results: ThresholdResult[];
  bestThreshold: ThresholdResult;
  processingTime: number;
}

// Sequence sensitivity analysis types
export interface SequenceSensitivityRequest {
  fileId: string;
  sequenceLengthRange: [number, number];
  winThreshold: number;
  analysisMode: AnalysisMode;
}

export interface SequenceResult {
  sequenceLength: number;
  accuracy: number;
  totalPredictions: number;
  skipRate: number;
  stabilityScore: number;
  processingTime: number;
}

export interface SequenceSensitivityResult {
  results: SequenceResult[];
  optimalLength: number;
  sweetSpotRange: [number, number];
  stabilityScore: number;
}

// Progress callback type
export type ProgressCallback = (progress: { completed: number; total: number }) => void;

// Performance heatmap types
export interface HeatmapRequest {
  fileId: string;
  thresholdRange: [number, number];
  thresholdStep: number;
  sequenceLengthRange: [number, number];
  analysisMode: AnalysisMode;
}

export interface HeatmapCell {
  threshold: number;
  sequenceLength: number;
  accuracy: number;
  totalPredictions: number;
  skipRate: number;
}

export interface HeatmapResult {
  cells: HeatmapCell[];
  bestConfiguration: HeatmapCell;
  processingTime: number;
}

// Service response type
export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export class OptimizationService {
  /**
   * Run multi-threshold optimization to find the best win threshold
   */
  static async runMultiThresholdOptimization(
    request: MultiThresholdRequest,
    progressCallback?: ProgressCallback
  ): Promise<ServiceResponse<MultiThresholdResult>> {
    try {
      // Get user for authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('multi-threshold-optimization', {
        body: request
      });

      if (error) {
        console.error('Multi-threshold optimization error:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      // Store optimization session in database
      const { error: saveError } = await supabase
        .from('optimization_sessions')
        .insert([{
          file_id: request.fileId,
          user_id: user.id,
          optimization_type: 'multi_threshold',
          configuration: request,
          results: data.result,
          best_configuration: {
            threshold: data.result.bestThreshold.threshold,
            accuracy: data.result.bestThreshold.accuracy
          },
          recommendation_confidence: data.result.bestThreshold.recommendationScore,
          processing_time_ms: data.result.processingTime
        }]);

      if (saveError) {
        console.error('Error saving optimization session:', saveError);
        // Don't fail the entire operation for save errors
      }

      return { success: true, data: data.result };
    } catch (error) {
      console.error('Multi-threshold optimization service error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Run sequence sensitivity analysis to find optimal sequence length
   */
  static async runSequenceSensitivityAnalysis(
    request: SequenceSensitivityRequest,
    progressCallback?: ProgressCallback
  ): Promise<ServiceResponse<SequenceSensitivityResult>> {
    try {
      // Get user for authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('sequence-sensitivity-analysis', {
        body: request
      });

      if (error) {
        console.error('Sequence sensitivity analysis error:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      // Store optimization session in database
      const { error: saveError } = await supabase
        .from('optimization_sessions')
        .insert([{
          file_id: request.fileId,
          user_id: user.id,
          optimization_type: 'sequence_sensitivity',
          configuration: request,
          results: data.result,
          best_configuration: {
            sequenceLength: data.result.optimalLength
          },
          recommendation_confidence: data.result.stabilityScore,
          processing_time_ms: data.result.results.reduce((sum, r) => sum + r.processingTime, 0)
        }]);

      if (saveError) {
        console.error('Error saving optimization session:', saveError);
        // Don't fail the entire operation for save errors
      }

      return { success: true, data: data.result };
    } catch (error) {
      console.error('Sequence sensitivity analysis service error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get optimization history for a file
   */
  static async getOptimizationHistory(
    fileId: string
  ): Promise<ServiceResponse<any[]>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('optimization_sessions')
        .select('*')
        .eq('file_id', fileId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching optimization history:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Get optimization history error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Run performance heatmap generation
   */
  static async runPerformanceHeatmap(
    request: HeatmapRequest,
    progressCallback?: ProgressCallback
  ): Promise<ServiceResponse<HeatmapResult>> {
    try {
      // Get user for authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('performance-heatmap-generator', {
        body: request
      });

      if (error) {
        console.error('Performance heatmap generation error:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      // Store optimization session in database
      const { error: saveError } = await supabase
        .from('optimization_sessions')
        .insert([{
          file_id: request.fileId,
          user_id: user.id,
          optimization_type: 'heatmap',
          configuration: request,
          results: data.result,
          best_configuration: {
            threshold: data.result.bestConfiguration.threshold,
            sequenceLength: data.result.bestConfiguration.sequenceLength,
            accuracy: data.result.bestConfiguration.accuracy
          },
          recommendation_confidence: data.result.bestConfiguration.accuracy,
          processing_time_ms: data.result.processingTime
        }]);

      if (saveError) {
        console.error('Error saving optimization session:', saveError);
        // Don't fail the entire operation for save errors
      }

      return { success: true, data: data.result };
    } catch (error) {
      console.error('Performance heatmap service error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Run automated AI optimization to find best configuration
   */
  static async runAutomatedOptimization(
    request: AutoOptimizationRequest,
    progressCallback?: ProgressCallback
  ): Promise<ServiceResponse<AutoOptimizationResult>> {
    try {
      // Get user for authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('automated-optimizer', {
        body: request
      });

      if (error) {
        console.error('Automated optimization error:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      // Store optimization session in database
      const { error: saveError } = await supabase
        .from('optimization_sessions')
        .insert([{
          file_id: request.fileId,
          user_id: user.id,
          optimization_type: 'auto_optimize',
          configuration: request,
          results: data.result,
          best_configuration: {
            threshold: data.result.bestThreshold,
            sequenceLength: data.result.bestSequenceLength,
            accuracy: data.result.expectedAccuracy,
            confidence: data.result.confidence
          },
          recommendation_confidence: data.result.statisticalSignificance,
          processing_time_ms: data.result.processingTime
        }]);

      if (saveError) {
        console.error('Error saving optimization session:', saveError);
        // Don't fail the entire operation for save errors
      }

      return { success: true, data: data.result };
    } catch (error) {
      console.error('Automated optimization service error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Delete an optimization session
   */
  static async deleteOptimizationSession(
    sessionId: string
  ): Promise<ServiceResponse<void>> {
    try {
      const { error } = await supabase
        .from('optimization_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        console.error('Error deleting optimization session:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Delete optimization session error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
}

// Additional types for automated optimization
export interface AutoOptimizationRequest {
  fileId: string;
  analysisMode: AnalysisMode;
  maxSequenceLength: number;
  thresholdRange: [number, number];
  targetMetric: 'accuracy' | 'risk_adjusted' | 'precision';
}

export interface AutoOptimizationResult {
  bestThreshold: number;
  bestSequenceLength: number;
  expectedAccuracy: number;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  statisticalSignificance: number;
  riskAdjustedScore: number;
  alternativeConfigs: Array<{
    threshold: number;
    sequenceLength: number;
    accuracy: number;
    tradeoff: string;
  }>;
  processingTime: number;
}